import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { BiCheckCircle } from "react-icons/bi"; // Ensure you have this import for the icon
import toast, { Toaster } from "react-hot-toast";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers";
import "../assets/css/theme-table-header.css";
import {
  EDIT_CUSTOM_ORDER_LINE_ERROR_OPTIONS,
  collectLineItemFieldErrors,
  lineCellErrorStyle,
  pruneLineFieldErrors,
} from "../utils/lineItemFieldErrors";
import {
  getUserData,
  node_imageBaseUrl,
} from "./const";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
} from "../utils/mobileInput";
import {
  listBankAccounts,
  getErpAuthState,
  listBillTypes,
  showCustomOrder,
  isErpApiSuccess,
  updateCustomOrderMultipartForm,
} from "../erpClient";
import { useLocation, useNavigate } from "react-router-dom";
import useFillSalesFromCustomOrder from "../utils/useFillSalesFromCustomOrder";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import OtherAmountModal from "../components/OtherAmountModal";
import {
  calculateOtherAmountAmount as calculateAmount,
  calculateOtherAmountLessWeight as calculateLessWeight,
  sumOtherAmountEntries,
  mapApiChargesToOtherAmountEntries,
  mapOtherAmountEntriesForUpdate,
} from "../utils/otherAmountCalculations";
import { handleRoundOffInputChange } from "../utils/roundOffInput";
import {
  mergeProductGroupOptions,
  searchProductGroupSelectOptions,
} from "../utils/productGroupOptions";
import { numPool } from "../utils/productGroupPool";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import PaymentSection, {
  PAYMENT_METHOD_LABELS,
} from "../components/PaymentSection";

import Select from "react-select";
import {
  billTypeHeaderSelectStyles,
  billTypeHeaderSelectWrapStyle,
} from "../utils/billTypeHeaderSelectStyles";
import {
  buildCustomOrderUpdatePaymentLines,
  createCustomOrderIdempotencyKey,
  mapShowPaymentsToCustomOrderPaymentRows,
  resolveCustomOrderShowPayload,
} from "../utils/customOrdersErpAdapter";

const EDIT_CUSTOM_ORDER_FETCH_DEDUPE_MS = 2000;

let customOrderShowPromise = null;
let customOrderShowCache = null;
let customOrderShowCacheId = null;
let customOrderShowCacheAt = 0;

let bankAccountsFetchPromise = null;
let bankAccountsFetchCache = null;
let bankAccountsFetchCacheAt = 0;

let billTypesFetchPromise = null;
let billTypesFetchCache = null;
let billTypesFetchCacheAt = 0;

function isCustomOrderShowSuccess(res) {
  return (
    isErpApiSuccess(res) ||
    res?.message === "radha" ||
    res?.data != null ||
    res?.custom_order_details != null
  );
}

async function requestCustomOrderShowForEdit(customOrderId, { force = false } = {}) {
  const id = String(customOrderId);
  const now = Date.now();

  if (!force) {
    if (customOrderShowPromise) return customOrderShowPromise;
    if (
      customOrderShowCache &&
      customOrderShowCacheId === id &&
      now - customOrderShowCacheAt < EDIT_CUSTOM_ORDER_FETCH_DEDUPE_MS
    ) {
      return customOrderShowCache;
    }
  } else {
    customOrderShowPromise = null;
    customOrderShowCache = null;
    customOrderShowCacheId = null;
    customOrderShowCacheAt = 0;
  }

  customOrderShowPromise = showCustomOrder({ id: customOrderId })
    .then((res) => {
      if (isCustomOrderShowSuccess(res)) {
        customOrderShowCache = res;
        customOrderShowCacheId = id;
        customOrderShowCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      customOrderShowPromise = null;
    });

  return customOrderShowPromise;
}

async function requestBankAccountsForEdit({ force = false } = {}) {
  const now = Date.now();

  if (!force) {
    if (bankAccountsFetchPromise) return bankAccountsFetchPromise;
    if (
      bankAccountsFetchCache &&
      now - bankAccountsFetchCacheAt < EDIT_CUSTOM_ORDER_FETCH_DEDUPE_MS
    ) {
      return bankAccountsFetchCache;
    }
  } else {
    bankAccountsFetchPromise = null;
    bankAccountsFetchCache = null;
    bankAccountsFetchCacheAt = 0;
  }

  bankAccountsFetchPromise = listBankAccounts()
    .then((res) => {
      if (res?.success) {
        bankAccountsFetchCache = res;
        bankAccountsFetchCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      bankAccountsFetchPromise = null;
    });

  return bankAccountsFetchPromise;
}

async function requestBillTypesForEdit({ force = false } = {}) {
  const now = Date.now();

  if (!force) {
    if (billTypesFetchPromise) return billTypesFetchPromise;
    if (
      billTypesFetchCache &&
      now - billTypesFetchCacheAt < EDIT_CUSTOM_ORDER_FETCH_DEDUPE_MS
    ) {
      return billTypesFetchCache;
    }
  } else {
    billTypesFetchPromise = null;
    billTypesFetchCache = null;
    billTypesFetchCacheAt = 0;
  }

  billTypesFetchPromise = listBillTypes()
    .then((res) => {
      if (isErpApiSuccess(res)) {
        billTypesFetchCache = res;
        billTypesFetchCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      billTypesFetchPromise = null;
    });

  return billTypesFetchPromise;
}

function pgDropdownLabel(item) {
  if (!item || typeof item !== "object") return "";
  if (item.name) return String(item.name);
  return String(item.label || "")
    .split(" — pool:")[0]
    .trim();
}

const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "100%",
    height: "100%",
    width: "100%",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": {
      border: "none",
      cursor: "pointer",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "100%",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#000",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#000",
    fontSize: "0.85rem",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "100%",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    color: "#6c757d",
    display: "none", // Hide the dropdown arrow to look more like a plain cell until interacted
  }),
  indicatorSeparator: (base) => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    width: "100%",
    minWidth: "150px", // Ensure dropdown is wide enough
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.85rem",
    padding: "8px 12px",
    backgroundColor: state.isSelected
      ? "#e9ecef"
      : state.isFocused
        ? "#f8f9fa"
        : "white",
    color: "#000",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#e9ecef",
    },
  }),
};


const EditCustomOrderEntry = ({ masterGoldRate, masterSilverRate }) => {
  const productGroupOptionsCacheRef = useRef([]);
  const productGroupUserSearchRef = useRef(false);
  const { buttonClass, tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const location = useLocation();
  const navigate = useNavigate();
  // Add this at the very top of the component
  // Add new function to calculate per gram rate
  const calculatePerGramRate = (rate, metalType) => {
    if (metalType === "Gold") {
      return rate / 10;
    } else if (metalType === "Silver") {
      return rate / 1000;
    }
    return rate;
  };

  // Calculate rate based on purity
  const calculatePurityBasedRate = (purity, rate, metalType) => {
    // Extract the karat/purity value from string (e.g. "18K" -> 18 or "925" -> 925)
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return rate; // If purity is invalid, return base rate

    if (metalType === "Gold") {
      // Always show per gram: (masterGoldRate / 10) * (purity / 24)
      return (rate / 10) * (purityValue / 24);
    } else if (metalType === "Silver") {
      // For silver, use the formula: X Purity Silver Price per gram = (999 Silver Price × (X/999)) / 1000
      return (rate * purityValue) / 999 / 1000;
    }

    return rate; // Default case if metal type is not recognized
  };

  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "tag_no",
      label: "Tag No",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.tag_no}
          onChange={(e) => onChange(row.id, "tag_no", e.target.value)}
        />
      ),
    },
    {
      id: "productGroup",
      label: "Product Group",
      width: "160px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },
    {
      id: "design",
      label: "Design",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.design}
          onChange={(e) => onChange(row.id, "design", e.target.value)}
        />
      ),
    },
    {
      id: "size",
      label: "Size",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.size}
          onChange={(e) => onChange(row.id, "size", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: "GWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWt",
      label: "LWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWt}
          onChange={(e) => onChange(row.id, "lsWt", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "NWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "itRateOn",
      label: "ItRateOn",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.itRateOn}
          onChange={(e) => onChange(row.id, "itRateOn", e.target.value)}
        />
      ),
    },
    {
      id: "itRate",
      label: "ItRate",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itRate}
          onChange={(e) => onChange(row.id, "itRate", e.target.value)}
        />
      ),
    },
    {
      id: "itAmt",
      label: "ItAmt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itAmt}
          onChange={(e) => onChange(row.id, "itAmt", e.target.value)}
        />
      ),
    },
    {
      id: "lType",
      label: "Lbr Type",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.lType}
          onChange={(e) => onChange(row.id, "lType", e.target.value)}
        />
      ),
    },
    {
      id: "lbrPercentage",
      label: "Lbr %",
      width: "70px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrPercentage}
          onChange={(e) => onChange(row.id, "lbrPercentage", e.target.value)}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "Lbr Rate",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
        />
      ),
    },
    {
      id: "lbrAmt",
      label: "Lbr Amt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrAmt}
          onChange={(e) => onChange(row.id, "lbrAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "OthAmt",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "mrp",
      label: "MRP",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.mrp}
          onChange={(e) => onChange(row.id, "mrp", e.target.value)}
        />
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onChange(row.id, "image", e.target.files[0])}
        />
      ),
    },
    {
      id: "totalAmt",
      label: "TotalAmt",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.totalAmt}
          onChange={(e) => onChange(row.id, "totalAmt", e.target.value)}
        />
      ),
    },
  ];

  // Define payment columns structure
  const PAYMENT_COLUMNS = [
    { id: "no", label: "No.", width: "30px", value: (row, index) => index + 1 },
    {
      id: "pay_mode",
      label: "Mode",
      width: "140px",
      value: (row, onChange) => {
        // Get all currently selected methods except the current row's method
        const selectedMethods = paymentRows
          .filter((pr) => pr.id !== row.id)
          .map((pr) => pr.pay_mode);

        // Filter available methods to show only unselected ones and current row's method
        const availableMethods = AVAILABLE_PAYMENT_METHODS.filter(
          (method) =>
            !selectedMethods.includes(method) || method === row.pay_mode,
        );

        const options = availableMethods.map((method) => ({
          value: method,
          label: PAYMENT_METHOD_LABELS[method],
        }));

        const selectedOption = options.find(
          (opt) => opt.value === row.pay_mode,
        );

        return (
          <Select
            styles={customSelectStyles}
            value={selectedOption || null}
            onChange={(selected) =>
              onChange(row.id, "pay_mode", selected ? selected.value : "")
            }
            options={options}
            placeholder="Select Method"
            menuPortalTarget={document.body}
          />
        );
      },
    },
    {
      id: "type",
      label: "Type",
      width: "140px",
      value: (row, onChange) => {
        // Match EditInvoice / Sales: bank account id as value; synth option if API used category strings for bank_type
        let opts = Array.isArray(paymentTypes) ? [...paymentTypes] : [];
        if (
          row.bankDisplayName &&
          row.type &&
          String(row.type).startsWith("__acctname__:")
        ) {
          const synth = { value: row.type, label: row.bankDisplayName };
          if (!opts.some((o) => String(o.value) === String(synth.value))) {
            opts = [synth, ...opts];
          }
        }
        if (
          row.bankDisplayName &&
          row.type != null &&
          String(row.type).trim() !== "" &&
          /^\d+$/.test(String(row.type).trim())
        ) {
          const synth = {
            value: String(row.type).trim(),
            label: row.bankDisplayName,
          };
          if (!opts.some((o) => String(o.value) === String(synth.value))) {
            opts = [synth, ...opts];
          }
        }
        const selectedOption = opts.find(
          (opt) => String(opt.value) === String(row.type),
        );

        return (
          <Select
            styles={customSelectStyles}
            value={selectedOption || null}
            onChange={(selected) =>
              onChange(row.id, "type", selected ? selected.value : "")
            }
            options={opts}
            placeholder="Select Type"
            menuPortalTarget={document.body}
            onMenuOpen={() => {
              if (!paymentTypes?.length) void fetchPaymentTypes();
            }}
          />
        );
      },
    },

    {
      id: "remarks",
      label: "Remarks",
      width: "180px",
      value: (row, onChange) => (
        <input
          type="text"
          className={`form-control border-0 rounded-0 ${buttonClass}`}
          style={{
            fontSize: "0.85rem",
            padding: "0.4rem 0.5rem",
            height: "100%",
          }}
          // placeholder="Remarks"
          value={row.remarks}
          onChange={(e) => onChange(row.id, "remarks", e.target.value)}
        />
      ),
    },
    {
      id: "amount",
      label: "Amt",
      width: "110px",
      value: (row, onChange, onFocus) => (
        <input
          type="number"
          className={`form-control border-0 rounded-0 ${buttonClass}`}
          style={{
            fontSize: "0.85rem",
            padding: "0.4rem 0.5rem",
            height: "100%",
          }}
          placeholder="0.00"
          value={row.amount}
          onChange={(e) => onChange(row.id, "amount", e.target.value)}
          onFocus={() => onFocus && onFocus(row.id)}
        />
      ),
    },
  ];

  // Fetch Customers from API
  const fetchCustomer = async () => {
    setuserss([]);
  };

  useEffect(() => {
    fetchCustomer();
  }, []);

  // Listen for API URL changes - navigate back to table
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in EditCustomOrderEntry, navigating back...",
        event.detail,
      );
      toast("Database switched. Returning to orders table.");
      navigate("/customorderentrytable");
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [navigate]);

  /** Infer metal label for dropdown metadata (same heuristics as old listProductGroups path). */
  const inferMetalFromProductGroupLabel = (label) => {
    const s = String(label || "").toLowerCase();
    if (s.includes("gold")) return "Gold";
    if (s.includes("silver")) return "Silver";
    if (s.includes("platinum")) return "Platinum";
    return "";
  };

  /** Seed product-group dropdown from showCustomOrder items (no list/show API on load). */
  const seedProductGroupsFromOrderItems = (customOrderData) => {
    const items = customOrderData?.custom_order_entry;
    if (!Array.isArray(items) || items.length === 0) return;
    const byId = new Map();
    items.forEach((row) => {
      const rawPid = row.product_group_id ?? row.product_group;
      const pid =
        rawPid != null &&
        rawPid !== "" &&
        Number.isFinite(Number(rawPid)) &&
        Number(rawPid) > 0
          ? rawPid
          : null;
      if (pid == null) return;
      const id = Number(pid);
      if (!Number.isFinite(id) || id <= 0) return;
      if (byId.has(id)) return;
      const name = String(row.product_group_name || "").trim();
      const label = name || String(id);
      byId.set(id, {
        value: id,
        label,
        short_code: row.short_code || "",
        purity: "",
        metal: inferMetalFromProductGroupLabel(label),
      });
    });
    const list = Array.from(byId.values());
    if (list.length === 0) return;

    productGroupOptionsCacheRef.current = mergeProductGroupOptions(
      productGroupOptionsCacheRef.current,
      list,
    );
  };

  // Function to send tag number to API

  const fetchPaymentTypes = async () => {
    try {
      const response = await requestBankAccountsForEdit();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance:
            bank.available_balance !== null &&
              bank.available_balance !== undefined
              ? bank.account_type === "metal"
                ? `${bank.available_balance} gm`
                : `₹${bank.available_balance}`
              : bank.account_type === "metal"
                ? "0 gm"
                : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);

        const paymentOptions = fetchedBankDetails.map((bank) => ({
          value: String(bank.id),
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
        }));
        setPaymentTypes(paymentOptions);
        return paymentOptions;
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        setPaymentTypes([]);
        return [];
      } else if (response && response.success === false) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error(response.message || "Failed to fetch bank details");
        return [];
      } else {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error("Unexpected response format from server");
        return [];
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      setPaymentTypes([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details",
      );
      return [];
    }
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const [bankAccounts, setBankAccounts] = useState([]);

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    design: [],
    lType: ["NWt", "GWt", "Pcs", "Fixed"], // Keeping this as it is since it's not from API
    itRateOn: ["NWt", "GWt"], // Adding new options for ItRateOn
  });

  // Columns that should have dropdown functionality
  const dropdownColumns = ["productGroup", "lType", "rateOn", "itRateOn"];

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: col.id === "itRateOn" ? "NWt" : "",
    // Add imageFile property for image column
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemFieldErrors, setItemFieldErrors] = useState({});

  useEffect(() => {
    setItemFieldErrors((prev) =>
      pruneLineFieldErrors(
        prev,
        collectLineItemFieldErrors(
          tableData,
          EDIT_CUSTOM_ORDER_LINE_ERROR_OPTIONS,
        ),
      ),
    );
  }, [tableData]);

  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false);
  const [productGroupPoolHeader, setProductGroupPoolHeader] = useState(null);
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const tooltipRefs = useRef({}); // Add refs for tooltips

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    zoomLevel: 1,
  });

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  const applyPoolHeaderFromGroup = (group) => {
    if (!group) {
      setProductGroupPoolHeader(null);
      return;
    }
    setProductGroupPoolHeader({
      pcs: numPool(group.pool_pcs),
      nwt: numPool(group.pool_nwt),
    });
  };

  const getProductGroupOptionsList = () =>
    mergeProductGroupOptions(
      productGroupOptionsCacheRef.current,
      dropdownData.productGroup,
    );

  const fetchProductGroupByPrefix = async (prefix = "") => {
    if (!productGroupUserSearchRef.current) {
      return [];
    }

    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      return [];
    }

    if (lastSearchedProductGroupPrefix === trimmed) {
      setProductGroupApiCalled(true);
      return dropdownData.productGroup;
    }

    setProductGroupSearchLoading(true);
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        opts,
      );
      setDropdownData((prev) => ({
        ...prev,
        productGroup: opts,
      }));
      setLastSearchedProductGroupPrefix(trimmed);
      setProductGroupApiCalled(true);
      if (opts.length > 0) {
        applyPoolHeaderFromGroup(opts[0]);
      }
      return opts;
    } catch (error) {
      console.error("Error searching product groups:", error);
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(true);
      return [];
    } finally {
      setProductGroupSearchLoading(false);
    }
  };

  const [billTypes, setBillTypes] = useState([]);
  const [selectedBillTypeId, setSelectedBillTypeId] = useState("");
  const [selectedBillTypeName, setSelectedBillTypeName] = useState("GST");
  const [estimateValue, setEstimateValue] = useState(0);
  const selectedBillTypeIdRef = useRef("");
  const [billDate, setBillDate] = useState("");
  // Add expDate state
  const [expDate, setExpDate] = useState("");

  // Add customer states
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerMobileDropdownActive, setCustomerMobileDropdownActive] =
    useState(false);
  const [customerMobileHighlightedIndex, setCustomerMobileHighlightedIndex] =
    useState(0);
  const customerMobileDropdownRef = useRef(null);
  const customerMobileInputRef = useRef(null);
  const customerMobileItemsRef = useRef([]);

  const [customerName, setCustomerName] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");
  const [customerNameDropdownActive, setCustomerNameDropdownActive] =
    useState(false);
  const [customerNameHighlightedIndex, setCustomerNameHighlightedIndex] =
    useState(0);
  const customerNameDropdownRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const customerNameItemsRef = useRef([]);

  const [customerAddress, setCustomerAddress] = useState("");
  const [customerArea, setCustomerArea] = useState("");
  const [customerCity, setCustomerCity] = useState("");

  // Add new state for customer details modal
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
  });

  // Add state and city related state variables
  const { states, cities, selectedState, setSelectedState } = useIndiaStateCity();

  // Add new state for tracking if customer is being added manually
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);
  const [userss, setuserss] = useState([]);

  // Mock customer data - replace with real API data
  const CUSTOMERS = userss.map((user) => ({
    id: user.id,
    name: user.fullName,
    fullName: user.fullName,
    mobile: user.mobile,
    email: user.email,
    city: user.city,
    address: user.address,
    area: user.area,
    birthDate: user.birthDate,
    maritalStatus: user.maritalStatus,
    anniversaryDate: user.anniversaryDate,
    balance: user.balance,
    saleBalance: user.saleBalance,
    companyName: user.companyName,
    companyAddress: user.companyAddress,
    gstNumber: user.gstNumber,
    aadharNo: user.aadharNo,
    panNo: user.panNo,
    reference: user.reference,
    state: user.state,
  }));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    selectedBillTypeIdRef.current = selectedBillTypeId;
  }, [selectedBillTypeId]);

  const applyBillTypeSelection = (billType) => {
    const id = billType?.id != null ? String(billType.id) : "";
    const name = String(billType?.name || "").trim();
    const isQuotation = name.toLowerCase() === "quotation";
    selectedBillTypeIdRef.current = id;
    setSelectedBillTypeId(id);
    setSelectedBillTypeName(name || "GST");
    setEstimateValue(isQuotation ? 1 : 0);
    setGstPercent(isQuotation ? "0" : "3");
  };

  useEffect(() => {
    const fetchBillTypeOptions = async () => {
      try {
        const response = await requestBillTypesForEdit();
        const rows = Array.isArray(response?.data?.bill_types)
          ? response.data.bill_types
          : [];
        if (!isErpApiSuccess(response) || !Array.isArray(rows)) return;

        const mapped = rows.map((row) => ({
          id: row?.id,
          name: String(row?.name || "").trim(),
        }));
        setBillTypes(mapped);

        if (!selectedBillTypeIdRef.current) {
          const gstType =
            mapped.find((item) => item.name.toLowerCase() === "gst") ||
            mapped[0];
          if (gstType) {
            applyBillTypeSelection(gstType);
          }
        }
      } catch (error) {
        console.error("Error fetching bill types:", error);
      }
    };

    fetchBillTypeOptions();
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
    setExpDate(""); // Initialize expDate to today
  }, []);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };
  // Add handler for expDate
  const handleExpDateChange = (e) => {
    setExpDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const CHARGE_TYPES = [
    {
      name: "Hallmark Charges",
      codes: ["HC", "HM"],
    },
    {
      name: "Stone Charges",
      codes: ["SC"],
    },
    {
      name: "Diamond Charges",
      codes: ["DC"],
    },
    {
      name: "Jadtar Charges",
      codes: ["JC"],
    },
    {
      name: "Customization Charges",
      codes: ["CC"],
    },
    {
      name: "Engraving Charges",
      codes: ["EC"],
    },
    {
      name: "Polishing Charges",
      codes: ["PC"],
    },
    {
      name: "Electroplating Charges",
      codes: ["EPC"],
    },
    {
      name: "Antique/Oxidized Finish Charges",
      codes: ["AFC"],
    },
    {
      name: "Kadha/Hollow Jewellery Filling Charges",
      codes: ["KFC"],
    },
    {
      name: "Melting & Refining Charges",
      codes: ["MRC"],
    },
    {
      name: "Weighing & Testing Charges",
      codes: ["WTC"],
    },
    {
      name: "Laser Cutting Charges",
      codes: ["LCC"],
    },
    {
      name: "Handmade Labour Charges",
      codes: ["HLC"],
    },
    {
      name: "Antique Kundan Meena Charges",
      codes: ["AKMC"],
    },
    {
      name: "Threading & Bead Stringing Charges",
      codes: ["TBC"],
    },
    {
      name: "Enamel Meenakari Charges",
      codes: ["EMC"],
    },
    {
      name: "Embossing & Carving Charges",
      codes: ["ECC"],
    },
    {
      name: "Rhodium Charges",
      codes: ["RC"],
    },
  ];

  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = CHARGE_TYPES.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery),
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = CHARGE_TYPES.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery)),
      );
    }

    // Get matches by name
    const matchedByName = CHARGE_TYPES.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name),
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`,
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      // For existing entries
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      // For current entry
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (
      activeDropdown.type !== "productGroup" ||
      activeDropdown.row === null ||
      activeDropdown.col === null
    ) {
      return;
    }
    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    const filtered = getFilteredItems(cell?.value, "productGroup");
    if (filtered[highlightedIndex]) {
      applyPoolHeaderFromGroup(filtered[highlightedIndex]);
    }
  }, [highlightedIndex, activeDropdown, dropdownData.productGroup]);

  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container for dropdown
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  // Function to calculate Labor Amount using Lbr % (based on itRate per gram — same as Sales.jsx)
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    itRatePerGram,
  ) => {
    return (
      (lbrPercentage / 100) *
      baseWeight *
      (parseFloat(itRatePerGram) || 0)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value) => {
    const newTableData = [...tableData];
    const cell = newTableData[rowIndex].data[colIndex];
    const id = cell.id;

    // Store previous value to check if it has changed
    const previousValue = cell.value;

    // If the changed column is lbrType
    if (id === "lbrType") {
      cell.value = value;

      // If Fixed is selected, clear lbrPercentage and lbrRate
      if (value === "Fixed") {
        const lbrPercentageCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrPercentage",
        );
        const lbrRateCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrRate",
        );

        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
        if (lbrRateCell) {
          lbrRateCell.value = "";
        }
      }

      setTableData(newTableData);
      return;
    }

    // Check if value matches the validation pattern for this column
    const validationPatterns = {
      design: /^[a-zA-Z0-9\s\-\_]*$/, // Allow alphanumeric, spaces, hyphens, underscores
      size: /^\d*$/, // Only numbers
      pcs: /^\d*$/, // Only numbers
      lbrPercentage: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      othAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      mrp: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      grWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lsWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      netWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      totalAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
    };

    // If pattern exists and value doesn't match, don't update
    if (validationPatterns[id] && !validationPatterns[id].test(value)) {
      return;
    }

    if (id === "productGroup") {
      cell.value = value;
      setTableData(newTableData);

      if (value && value.length >= 3) {
        const knownOption = getProductGroupOptionsList().find(
          (pg) => pg.label === value || pg.name === value,
        );
        if (knownOption) {
          return;
        }
        if (!productGroupUserSearchRef.current) {
          return;
        }
        if (
          !lastSearchedProductGroupPrefix ||
          value !== lastSearchedProductGroupPrefix
        ) {
          setProductGroupApiCalled(false);
          fetchProductGroupByPrefix(value);
        }
      } else {
        setDropdownData((prev) => ({ ...prev, productGroup: [] }));
        setLastSearchedProductGroupPrefix("");
        setProductGroupApiCalled(false);
        setProductGroupPoolHeader(null);
      }
      return;
    }

    cell.value = value;

    // Handle calculations based on the changed field
    if (id === "grWt" || id === "lsWt") {
      // Calculate Net Weight when Gross Weight or Less Weight changes
      const gwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt",
      );
      const lwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lsWt",
      );

      if (gwCell && lwCell) {
        const grWt = parseFloat(gwCell.value) || 0;
        const lsWt = parseFloat(lwCell.value) || 0;
        const netWt = calculateNetWeight(grWt, lsWt);

        // Update the netWt cell
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt",
        );
        if (netWtCell) {
          netWtCell.value = netWt.toString();

          // If itRate exists, recalculate itAmt based on itRateOn
          const itRateCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRate",
          );
          const itRateOnCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRateOn",
          );

          if (itRateCell && itRateCell.value) {
            const itRate = parseFloat(itRateCell.value) || 0;
            const itRateOn = itRateOnCell?.value; // No default value

            // Determine which weight to use based on itRateOn
            let baseWeight = 0; // Initialize to 0, no default
            if (itRateOn === "NWt") {
              baseWeight = netWt;
            } else if (itRateOn === "GWt") {
              baseWeight = grWt;
            }

            const itAmt = (baseWeight * itRate).toFixed(2);

            // Update the itAmt cell
            const itAmtCell = newTableData[rowIndex].data.find(
              (cell) => cell.id === "itAmt",
            );
            if (itAmtCell) {
              itAmtCell.value = itAmt.toString();

              // Recalculate totalAmt
              calculateTotalAmount(newTableData, rowIndex);
            }
          }
        }
      }
    } else if (
      id === "netWt" ||
      id === "itRate" ||
      id === "grWt" ||
      id === "itRateOn"
    ) {
      // Calculate Item Amount when Net Weight, Gross Weight, Item Rate, or Item Rate On changes
      const netWtCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "netWt",
      );
      const grWtCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt",
      );
      const itRateCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRate",
      );
      const itRateOnCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRateOn",
      );

      if (netWtCell && itRateCell && itRateCell.value) {
        const netWt = parseFloat(netWtCell.value) || 0;
        const grWt = parseFloat(grWtCell?.value) || 0;
        const itRate = parseFloat(itRateCell.value) || 0;
        const itRateOn = itRateOnCell?.value || "NWt"; // Default to NWt if not specified

        // Determine which weight to use based on itRateOn
        let baseWeight = netWt; // Default to Net Weight
        if (itRateOn === "GWt") {
          baseWeight = grWt;
        }

        const itAmt = (baseWeight * itRate).toFixed(2);

        // Update the itAmt cell
        const itAmtCellNW = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itAmt",
        );
        if (itAmtCellNW) {
          itAmtCellNW.value = itAmt.toString();

          // Recalculate totalAmt
          calculateTotalAmount(newTableData, rowIndex);
        }
      }
    } else if (id === "lType") {
      // Handle LBR type change
      const lTypeCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lType",
      );
      if (lTypeCell) {
        lTypeCell.value = value;
      }
    } else if (id === "lbrAmt" || id === "othAmt" || id === "itAmt") {
      // Recalculate totalAmt when any of the amount fields change
      calculateTotalAmount(newTableData, rowIndex);
    }

    // Handle LBR percentage and rate calculations
    if (id === "lType" || id === "lbrPercentage" || id === "lbrRate") {
      // Track which labor field was last changed
      if (id === "lbrPercentage" || id === "lbrRate") {
        newTableData[rowIndex].lastChangedLaborField = id;
      }
      handleLaborCalculations(newTableData, rowIndex);
    }

    setTableData(newTableData);
  };

  // New function to calculate total amount
  const calculateTotalAmount = (tableData, rowIndex) => {
    const itAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "itAmt",
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt",
    );
    const othAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "othAmt",
    );

    const itAmt = parseFloat(itAmtCell?.value) || 0;
    const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
    const othAmt = parseFloat(othAmtCell?.value) || 0;

    const totalAmt = (itAmt + lbrAmt + othAmt).toFixed(2);

    // Update the totalAmt cell
    const totalAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "totalAmt",
    );
    if (totalAmtCell) {
      totalAmtCell.value = totalAmt.toString();
    }
  };

  // Handle image upload
  const handleImageUpload = async (field, rowIndex) => {
    // Create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview and store the file in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image",
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field; // Store the actual file object
            imageCell.value = null; // Clear any previous uploaded image name
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Open image modal
  const openImageModal = (imageUrl) => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    setImageModal({
      isOpen: true,
      imageUrl,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal show={imageModal.isOpen} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                // Trigger file input to change the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl,
                  ),
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image",
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                // Find and remove the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl,
                  ),
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image",
                  );
                  if (colIndex !== -1) {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      const imageCell = updatedData[rowIndex].data.find(
                        (c) => c.id === "image",
                      );
                      if (imageCell) {
                        imageCell.value = "";
                        imageCell.imageFile = null;
                        imageCell.imagePreview = null;
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal"),
    );
  };

  const handleAddRow = () => {
    // Flag to prevent multiple simultaneous calls
    if (window.isAddingRow) return;
    window.isAddingRow = true;

    // Get the current length before adding
    const newRowIndex = tableData.length;

    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: col.id === "itRateOn" ? "NWt" : "",
          // Add imageFile property for image column
          ...(col.id === "image"
            ? { imageFile: null, imagePreview: null }
            : {}),
        })),
      },
    ]);

    // Allow adding again after a short delay
    setTimeout(() => {
      window.isAddingRow = false;
    }, 100);
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    const columnId = COLUMNS[colIndex]?.id;
    const value =
      typeof selectedItem === "object"
        ? selectedItem.name || selectedItem.label || ""
        : selectedItem;

    handleInputChange(rowIndex, colIndex, value);
    setActiveDropdown({ row: null, col: null, type: null });

    if (columnId === "productGroup" && typeof selectedItem === "object") {
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        [selectedItem],
      );
      applyPoolHeaderFromGroup(selectedItem);
    }

    // Calculate purity-based rate when product group is selected
    if (columnId === "productGroup") {
      const selectedProductGroup =
        typeof selectedItem === "object" && selectedItem?.value != null
          ? selectedItem
          : getProductGroupOptionsList().find((pg) => pg.label === value);
      if (selectedProductGroup && selectedProductGroup.purity) {
        // Calculate the purity-based rate
        const baseRate =
          selectedProductGroup.metal === "Gold"
            ? masterGoldRate
            : masterSilverRate;
        const rate24k = calculatePerGramRate(baseRate);
        const purityBasedRate = calculatePurityBasedRate(
          selectedProductGroup.purity,
          rate24k,
          selectedProductGroup.metal,
        );

        // Find the itRate column index
        const itRateColIndex = COLUMNS.findIndex((col) => col.id === "itRate");
        if (itRateColIndex !== -1) {
          // Update the itRate with the calculated purity-based rate
          handleInputChange(
            rowIndex,
            itRateColIndex,
            purityBasedRate.toFixed(2),
          );
        }
      }
    }

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Normalize search text
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // If no search, return all
    if (!normalizedSearchText) return dropdownData[columnType];

    // Special case: array of strings
    if (columnType === "lType" || columnType === "itRateOn") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText),
      );
    }

    // Special case: productGroup — API search results (no client-side re-filter)
    if (columnType === "productGroup") {
      return dropdownData[columnType] || [];
    }

    // Default handling
    return dropdownData[columnType].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return item.toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex],
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);
    // Reset refs array
    dropdownItemsRef.current = [];

    // Calculate max width based on content
    const maxWidth = Math.max(
      ...filtered.map((item) => {
        const tempSpan = document.createElement("span");
        tempSpan.style.fontSize = "0.85rem";
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.whiteSpace = "nowrap";

        // Safely get the label
        const itemLabel = typeof item === "object" ? item.label || "" : item;
        tempSpan.textContent = itemLabel;

        document.body.appendChild(tempSpan);
        const width = tempSpan.offsetWidth;
        document.body.removeChild(tempSpan);
        return width;
      }),
      dropdownPosition.width, // minimum width is input width
    );

    // Special styling for productGroup dropdown - match AddGirvi.jsx pattern
    const isProductGroup = activeDropdown.type === "productGroup";
    // For productGroup: use 5 items with 40px height each (matching AddGirvi.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = isProductGroup ? 40 : 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    // Calculate width for dropdowns - match AddGirvi.jsx pattern
    const adjustedWidth = isProductGroup
      ? Math.max(200, dropdownPosition.width)
      : Math.max(maxWidth + 10, dropdownPosition.width);

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${adjustedWidth}px`,
          minWidth: `${adjustedWidth}px`,
          maxWidth: `${adjustedWidth}px`,
          maxHeight: isProductGroup
            ? `${dropdownHeight + 16}px`
            : `${dropdownHeight}px`,
          height: isProductGroup
            ? `${dropdownHeight + 16}px`
            : `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: isProductGroup ? "visible" : "hidden",
          }}
        >
          {isProductGroup && productGroupSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((item, idx) => {
              const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? pgDropdownLabel(item)
                    : item.name || item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                    }`}
                  title={itemLabel}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: isProductGroup ? "normal" : "nowrap",
                    wordWrap: isProductGroup ? "break-word" : "normal",
                    overflow: isProductGroup ? "visible" : "hidden",
                    textOverflow: isProductGroup ? "clip" : "ellipsis",
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(idx);
                    if (isProductGroup) applyPoolHeaderFromGroup(item);
                  }}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item,
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : isProductGroup && !productGroupApiCalled ? null : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal"),
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    // If this is a dropdown column, show the dropdown
    const columnType = getColumnType(colIndex);
    if (isDropdownColumn(columnType)) {
      if (columnType === "productGroup") {
        const productGroupCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "productGroup",
        );
        const value = (productGroupCell?.value || "").toString().trim();
        const hasEnoughChars = value.length >= 3;
        if (!hasEnoughChars && value) {
          const selected = getProductGroupOptionsList().find(
            (pg) => pg.name === value || pg.label === value,
          );
          applyPoolHeaderFromGroup(selected);
        }
        const hasResults = getFilteredItems(value, "productGroup").length > 0;
        if (hasEnoughChars && (hasResults || productGroupApiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
      } else {
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  // Add helper function to open modal
  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt")
        .otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt").value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
          ...entry,
          rateOn: entry.rateOn || "", // Ensure rateOn is initialized
        }))
        : currentValue
          ? [
            {
              chargeType: "Direct Entry",
              pcs: "",
              ct: "",
              wt: "",
              conPercentage: "",
              rateOn: "",
              rate: "",
              amount: currentValue,
            },
          ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  // Add function to save other amount entries
  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    // If there's data in the current entry, validate and add it to entries
    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    // Calculate total amount
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );

    // Calculate total less weight
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    // Update table data with both values
    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      // Get all required values
      const grWtCell = row.data.find((cell) => cell.id === "grWt");
      const lTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrPercentageCell = row.data.find(
        (cell) => cell.id === "lbrPercentage",
      );
      const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
      const itRateCell = row.data.find((cell) => cell.id === "itRate");
      const itRateOnCell = row.data.find((cell) => cell.id === "itRateOn");
      const productGroupCell = row.data.find(
        (cell) => cell.id === "productGroup",
      );

      const grWt = parseFloat(grWtCell?.value) || 0;
      const lType = lTypeCell?.value;
      const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
      const lbrRate = parseFloat(lbrRateCell?.value) || 0;
      const itRate = parseFloat(itRateCell?.value) || 0;
      const itRateOn = itRateOnCell?.value;

      // Get metal type from product group
      const selectedProductGroup = dropdownData.productGroup.find(
        (pg) => pg.label === productGroupCell?.value,
      );
      const metalType = selectedProductGroup?.metal || "Gold";

      // Update Other Amount
      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      const lsWtCell = row.data.find((cell) => cell.id === "lsWt");
      const currentLsWtVal = parseFloat(lsWtCell?.value) || 0;
      const newLsWtVal = formatLessWeight(totalLessWeight);
      if (lsWtCell && totalLessWeight > 0) {
        lsWtCell.value = newLsWtVal;
      }

      const lwForCalc = totalLessWeight > 0 ? totalLessWeight : currentLsWtVal;
      const netWeight = calculateNetWeight(grWt, lwForCalc);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      // Calculate Item Amount based on ItRateOn
      const itAmtCell = row.data.find((cell) => cell.id === "itAmt");
      if (itRateCell && itRate && itAmtCell) {
        let baseWeight = 0;
        if (itRateOn === "NWt") {
          baseWeight = netWeight;
        } else if (itRateOn === "GWt") {
          baseWeight = grWt;
        }
        const itAmt = (baseWeight * itRate).toFixed(2);
        itAmtCell.value = itAmt;
      }

      // Calculate Labor Amount (same itRate-based logic as Sales.jsx)
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
      if (lbrAmtCell && !lbrAmtCell.isManual) {
        const lTypeUpper = (lType || "").toString().trim().toUpperCase();
        let baseWeight = 0;
        if (lTypeUpper === "NWT") {
          baseWeight = netWeight;
        } else if (lTypeUpper === "GWT") {
          baseWeight = grWt;
        } else if (lTypeUpper === "PCS") {
          const pcsCell = row.data.find((cell) => cell.id === "pcs");
          baseWeight = parseFloat(pcsCell?.value) || 0;
        }

        let lbrAmt = 0;
        if (lbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            itRate,
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }
        lbrAmtCell.value = Math.round(lbrAmt).toString();
      }

      // Calculate Total Amount
      const itAmt = parseFloat(itAmtCell?.value) || 0;
      const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
      const othAmt = parseFloat(othAmtCell?.value) || 0;
      const totalAmt = (itAmt + lbrAmt + othAmt).toFixed(2);

      const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
      if (totalAmtCell) {
        totalAmtCell.value = totalAmt;
      }

      return newData;
    });

    // Close modal after saving
    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Add function to handle column width changes
  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex]?.rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase()),
    );

    const dropdown = e.target.nextElementSibling;

    if (e.key === "Enter") {
      e.preventDefault();
      if (
        dropdown &&
        dropdown.style.display === "block" &&
        filtered.length > 0 &&
        filtered[highlightedIndex]
      ) {
        handleRateOnDropdown(
          rowIndex,
          filtered[highlightedIndex],
          isCurrentEntry,
        );
        setTimeout(() => {
          const row = e.target.closest("tr");
          const rateInput = row?.querySelectorAll("td")[7]?.querySelector("input");
          rateInput?.focus();
        }, 0);
      } else if (dropdown) {
        dropdown.style.display = "block";
        setHighlightedIndex(0);
      }
      return;
    }

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (dropdown && dropdown.style.display !== "block") {
          dropdown.style.display = "block";
        }
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        if (dropdown && dropdown.style.display !== "block") {
          dropdown.style.display = "block";
        }
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Escape":
        e.preventDefault();
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };


  const getRowEffectiveAmount = (row) => {
    if (!row || !row.data) return 0;

    const mrpCell = row.data.find((cell) => cell.id === "mrp");
    const mrpValue = mrpCell ? parseFloat(mrpCell.value) : NaN;
    const hasMrp =
      mrpCell && mrpCell.value !== "" && !isNaN(mrpValue) && mrpValue > 0;

    if (hasMrp) {
      return mrpValue;
    }

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const totalAmtValue = totalAmtCell ? parseFloat(totalAmtCell.value) : NaN;

    return isNaN(totalAmtValue) ? 0 : totalAmtValue;
  };

  const calculateTotal = () => {
    if (!tableData) return 0;
    return tableData.reduce((sum, row) => {
      const effectiveAmount = getRowEffectiveAmount(row);
      return sum + effectiveAmount;
    }, 0);
  };

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Helper to calculate labor amount from row itRate (aligned with Sales.jsx)
  const calculateLbrAmt = (baseWeight, lbrPercentage, lbrRate, itRatePerGram) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        itRatePerGram,
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };

  // Calculate GST amount based on percentage

  // Calculate grand total (total amount - discount + GST - round-off)

  // Calculate GST amount based on percentage of Final Amount
  const calculateGstAmount = () => {
    const finalAmount = calculateTotal();
    const gstPercentValue = parseFloat(gstPercent) || 0;
    const gstAmt = (finalAmount * gstPercentValue) / 100;
    return gstAmt;
  };

  // Calculate grand total (Final Amount + GST Amount - Round Off)
  const calculateGrandTotal = () => {
    const finalAmount = calculateTotal();
    const gstAmt = calculateGstAmount();
    const roundOff = parseFloat(roundOffAmount) || 0;
    return (finalAmount + gstAmt - roundOff).toFixed(2);
  };

  // Define product and setProductGroups using useState

  const [design, setDesign] = useState(""); // Initialize design state
  const [size, setSize] = useState(""); // Initialize size state
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [lType, setLType] = useState(""); // Initialize lType state
  const [lbrPercentage, setLbrPercentage] = useState(0); // Initialize lbrPercentage state
  const [lbrRate, setLbrRate] = useState(0); // Initialize lbrRate state
  const [lbrAmt, setLbrAmt] = useState(0); // Initialize lbramt state
  const [othamt, setOthAmt] = useState(0); // Initialize othAmt state
  const [mrp, setMrp] = useState(0); // Initialize mrp state
  const [image, setImage] = useState(null); // Initialize image state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [productGroup, setProductGroup] = useState(""); // Initialize productGroup state (assuming it's a string)
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roundOffAmount, setRoundOffAmount] = useState(""); // Change to string for input
  const [gstPercent, setGstPercent] = useState("3"); // GST percentage
  const [gstAmount, setGstAmount] = useState("0.00"); // GST amount
  const [note, setNote] = useState(""); // Add note state


  // Add resetForm function before handleSubmit
  const resetForm = () => {
    // Reset bill type and date
    setBillDate(new Date().toISOString().split("T")[0]);

    // Reset customer fields
    setCustomerMobile("");
    setCustomerMobileSearchText("");
    setCustomerName("");
    setCustomerNameSearchText("");
    setCustomerAddress("");
    setCustomerCity("");
    setSelectedCustomerId(null);
    setCustomerDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
    });

    // Reset table data
    setTableData([{ id: 1, data: initialRowData }]);
    setItemFieldErrors({});

    // Reset payment rows
    setPaymentRows([
      {
        id: 1,
        pay_mode: "cash", // Pre-selected cash
        type: "",
        remarks: "",
        amount: "",
      },
      {
        id: 2,
        pay_mode: "cheque", // Pre-selected cheque
        type: "",
        remarks: "",
        amount: "",
      },
      {
        id: 3,
        pay_mode: "bank_transfer", // Pre-selected bank transfer
        type: "",
        remarks: "",
        amount: "",
      },
    ]);


    // Reset other fields
    setRoundOffAmount("");
    setNote("");
    setFollowUpDate("");
    setPayableRows([
      {
        id: 1,
        pay_mode: "cash",
        type: "",
        remarks: "",
        amount: "",
      },
    ]);
    setExpDate(""); // Reset Exp Delivery
    setExpDueDate(""); // Reset global Exp Date
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const itemErrors = collectLineItemFieldErrors(
        tableData,
        EDIT_CUSTOM_ORDER_LINE_ERROR_OPTIONS,
      );
      const hasItemErrors = Object.keys(itemErrors).length > 0;
      setItemFieldErrors(hasItemErrors ? itemErrors : {});

      const userId =
        selectedCustomerId || storedCustomOrderData?.user_id || null;
      if (!userId) {
        toast.error("Invalid customer selected");
        return;
      }

      // Get logged-in user ID
      const legacyUser =
        (typeof getUserData === "function" ? getUserData() : null) || {};
      const erpUser = getErpAuthState?.()?.user || {};
      const by_user =
        legacyUser?.id ??
        legacyUser?.ua_id ??
        erpUser?.id ??
        erpUser?.ua_id ??
        erpUser?.user_id;
      if (!by_user) {
        toast.error("User not logged in");
        return;
      }

      // Ensure exp_delivery uses expDate and exp_date uses expDueDate
      const formattedExpDelivery = expDate
        ? new Date(expDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      const formattedExpDate = expDueDate
        ? new Date(expDueDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      if (hasItemErrors) {
        return;
      }

      const customOrderRows = [];
      const formData = new FormData();
      let hasValidRow = false;

      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          return acc;
        }, {});

        if (!rowData.productGroup) continue;

        hasValidRow = true;

        const existingEntry =
          storedCustomOrderData?.custom_order_entry?.[rowIndex] || {};

        const selectedProductGroup = getProductGroupOptionsList().find(
          (group) =>
            group.label === rowData.productGroup ||
            group.short_code === rowData.productGroup ||
            group.name === rowData.productGroup,
        );

        let productGroupId = selectedProductGroup?.value ?? null;
        if (productGroupId == null) {
          const fallbackRaw =
            existingEntry.product_group_id ?? existingEntry.product_group;
          const numericId = Number(fallbackRaw);
          if (Number.isFinite(numericId) && numericId > 0) {
            productGroupId = numericId;
          } else {
            throw new Error(`Row ${rowIndex + 1}: Product Group not found`);
          }
        }

        const otherAmtCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "othAmt",
        );
        const charges = mapOtherAmountEntriesForUpdate(
          otherAmtCell?.otherAmountEntries || [],
        );

        const tag_no = `ORD${String(rowIndex + 1).padStart(3, "0")}`;
        const imageCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "image"
        );
        const imageName = imageCell?.imageFile?.name || "";

        customOrderRows.push({
          tag_no,
          tag_no: rowData.tag_no || "",
          karigar_id: existingEntry.karigar_id || "",
          exp_date: existingEntry.exp_date || "",
          product_group: productGroupId,
          design: rowData.design,
          size: rowData.size,
          pcs: Number(rowData.pcs),
          gwt: rowData.grWt,
          lwt: rowData.lsWt || "",
          nwt: rowData.netWt,
          itrateon: rowData.itRateOn || "Net",
          itrate: rowData.itRate || "",
          itamt: rowData.itAmt || "",
          lbr_type: rowData.lType || "Rs",
          lbr_percent: rowData.lbrPercentage || "",
          lbr_rate: rowData.lbrRate || "",
          lbr_amt: rowData.lbrAmt || "",
          othamt: rowData.othAmt || "",
          mrp: rowData.mrp || "",
          totalamt: rowData.totalAmt || "",
          image: imageName,
          image_name: imageName,
          charges,
        });

        // Append image file
        if (imageCell && imageCell.imageFile) {
          formData.append(`image_${rowIndex}`, imageCell.imageFile);
        }
      }

      if (!hasValidRow) {
        toast.error("No valid product entries to submit");
        return;
      }

      const paymentPersistKeys = [];
      const needsBankLookup = paymentRows.some((row) => {
        const t = String(row?.type || "").toLowerCase();
        return (
          t === "old_gold" ||
          t === "old_silver" ||
          t === "kasar" ||
          t.startsWith("__acctname__:")
        );
      });
      let paymentTypesForSubmit = paymentTypes;
      if (needsBankLookup && paymentTypesForSubmit.length === 0) {
        const fetchedPaymentTypes = await fetchPaymentTypes();
        if (
          Array.isArray(fetchedPaymentTypes) &&
          fetchedPaymentTypes.length > 0
        ) {
          paymentTypesForSubmit = fetchedPaymentTypes;
        }
      }

      const customOrderPayments = buildCustomOrderUpdatePaymentLines(
        paymentRows,
        {
          paymentTypes: paymentTypesForSubmit,
          payModeLabels: PAYMENT_METHOD_LABELS,
          by_user,
          createKey: createCustomOrderIdempotencyKey,
          persistKeys: paymentPersistKeys,
        },
      );

      if (paymentPersistKeys.length > 0) {
        setPaymentRows((prev) => {
          const next = [...prev];
          paymentPersistKeys.forEach(({ rowId, key }) => {
            const idx = next.findIndex((r) => r.id === rowId);
            if (idx >= 0) {
              next[idx] = { ...next[idx], paymentIdempotencyKey: key };
            }
          });
          return next;
        });
      }

      const final_amt = Number(calculateTotal()) || 0;
      const customOrderPayload = customOrderRows.map((row) => ({
        tag_no: row.tag_no || "",
        karigar_id: row.karigar_id || null,
        exp_date: row.exp_date || formattedExpDate,
        product_group: Number(row.product_group) || row.product_group || null,
        design: row.design || "",
        size: row.size || "",
        pcs: Number(row.pcs) || 0,
        gwt: Number(row.gwt) || 0,
        lwt: Number(row.lwt) || 0,
        nwt: Number(row.nwt) || 0,
        itrateon: row.itrateon || "",
        itrate: Number(row.itrate) || 0,
        itamt: Number(row.itamt) || 0,
        lbr_type: row.lbr_type || "",
        lbr_percent: Number(row.lbr_percent) || 0,
        lbr_rate: Number(row.lbr_rate) || 0,
        lbr_amt: Number(row.lbr_amt) || 0,
        othamt: Number(row.othamt) || 0,
        mrp: row.mrp || "",
        totalamt: Number(row.totalamt) || 0,
        image: row.image || "",
        image_name: row.image_name || "",
        charges: Array.isArray(row.charges) ? row.charges : [],
      }));

      // Multipart payload keys expected by backend.
      formData.append("id", String(locationState?.customOrderId || ""));
      formData.append("customer_id", String(userId));
      formData.append("date", billDate || new Date().toISOString().split("T")[0]);
      formData.append(
        "bill_type_id",
        selectedBillTypeId ? String(Number(selectedBillTypeId)) : "",
      );
      formData.append("bill_type_name", selectedBillTypeName || "");
      formData.append("estimate", String(Number(estimateValue) || 0));
      formData.append("expected_delivery", formattedExpDelivery);
      formData.append("discount", "0");
      formData.append("tax_amount", String(Number(gstAmount) || 0));
      formData.append("round_off", String(Number(roundOffAmount) || 0));
      formData.append("grand_total", String(final_amt));
      formData.append("notes", note || "");
      if (followUpDate) {
        const formattedFollowupDate = new Date(followUpDate)
          .toISOString()
          .split("T")[0];
        formData.append("followup_date", formattedFollowupDate);
      } else {
        formData.append("followup_date", "");
      }
      formData.append("items", JSON.stringify(customOrderPayload));

      // Always send payments on update (same as EditInvoice) with existing line ids.
      formData.append("payments", JSON.stringify(customOrderPayments));

      if (!customOrderUpdateIdempotencyKeyRef.current) {
        customOrderUpdateIdempotencyKeyRef.current =
          createCustomOrderIdempotencyKey();
      }
      formData.append(
        "idempotency_key",
        customOrderUpdateIdempotencyKeyRef.current,
      );

      const customOrderUpdatePayload = { type: "update" };
      for (const [key, val] of formData.entries()) {
        if (key === "items" || key === "payments") {
          try {
            customOrderUpdatePayload[key] = JSON.parse(val);
          } catch {
            customOrderUpdatePayload[key] = val;
          }
        } else if (val instanceof File) {
          customOrderUpdatePayload[key] = {
            name: val.name,
            size: val.size,
            type: val.type,
          };
        } else {
          customOrderUpdatePayload[key] = val;
        }
      }
      console.log(
        "[EditCustomOrderEntry] custom order update payload:",
        customOrderUpdatePayload,
      );

      const data = await updateCustomOrderMultipartForm(formData);

      // Handle different response formats
      if (
        data === true ||
        data?.message === "radha" ||
        isErpApiSuccess(data)
      ) {
        customOrderUpdateIdempotencyKeyRef.current = null;
        toast.success("Order updated successfully");
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          resetForm();
          navigate("/customorderentrytable");
        }, 800);
      } else {
        console.error("API Response Error:", data);
        toast.error(data?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      console.error("Error Response:", error.response?.data);
      console.error("Error Status:", error.response?.status);
      console.error("Error Status Text:", error.response?.statusText);

      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to submit the form.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // New function to handle labor calculations (aligned with Sales.jsx — uses row itRate)
  const handleLaborCalculations = (tableData, rowIndex) => {
    const lTypeCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lType",
    );
    const lbrPercentageCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrPercentage",
    );
    const lbrRateCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrRate",
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt",
    );
    if (lbrAmtCell?.isManual) {
      return;
    }

    const lType = (lTypeCell?.value || "").toString().trim();
    const lTypeUpper = lType.toUpperCase();
    const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
    const lbrRate = parseFloat(lbrRateCell?.value) || 0;
    const itRateCellRef = tableData[rowIndex].data.find(
      (cell) => cell.id === "itRate",
    );
    const itRatePerGram = parseFloat(itRateCellRef?.value) || 0;

    // If the labor type is Fixed, clear the percentage and rate fields
    if (lTypeUpper === "FIXED") {
      if (lbrPercentageCell) lbrPercentageCell.value = "";
      if (lbrRateCell) lbrRateCell.value = "";
      return;
    }

    // Determine the base weight based on labor type (case-insensitive)
    let baseWeight = 0;
    if (lTypeUpper === "NWT") {
      const netWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "netWt",
      );
      baseWeight = parseFloat(netWtCell?.value) || 0;
    } else if (lTypeUpper === "GWT") {
      const grWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "grWt",
      );
      baseWeight = parseFloat(grWtCell?.value) || 0;
    } else if (lTypeUpper === "PCS") {
      const pcsCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "pcs",
      );
      baseWeight = parseFloat(pcsCell?.value) || 0;
    }

    let lbrAmt = 0;
    let lastChangedField = tableData[rowIndex].lastChangedLaborField;

    if (lbrPercentage === 0 && lbrRate === 0) {
      delete tableData[rowIndex].lastChangedLaborField;
      lastChangedField = null;
    }

    if (lastChangedField === "lbrPercentage") {
      if (lbrPercentage > 0) {
        lbrAmt = calculateLbrAmtWithPercentage(
          baseWeight,
          lbrPercentage,
          itRatePerGram,
        );
        if (lbrRateCell) {
          lbrRateCell.value = ((lbrPercentage / 100) * itRatePerGram).toFixed(
            2,
          );
        }
      } else {
        if (lbrRateCell) {
          lbrRateCell.value = "";
        }
        lbrAmt = 0;
      }
    } else if (lastChangedField === "lbrRate") {
      if (lbrRate > 0) {
        lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        if (lbrPercentageCell) {
          lbrPercentageCell.value = itRatePerGram
            ? ((lbrRate / itRatePerGram) * 100).toFixed(2)
            : "";
        }
      } else {
        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
        lbrAmt = 0;
      }
    } else if (!lastChangedField) {
      if (lbrPercentage > 0) {
        lbrAmt = calculateLbrAmtWithPercentage(
          baseWeight,
          lbrPercentage,
          itRatePerGram,
        );
      } else if (lbrRate > 0) {
        lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
      }
    }

    if (lbrAmtCell) {
      lbrAmtCell.value = Math.round(lbrAmt).toString();
      calculateTotalAmount(tableData, rowIndex);
    }
  };

  // Handle customer mobile input change
  const handleCustomerMobileChange = (e) => {
    const value = sanitizeMobileInput(e.target.value);
    setCustomerMobileSearchText(value);
    setCustomerMobile(value);
    setCustomerMobileDropdownActive(true);
    setCustomerMobileHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered customers based on mobile number
  const getFilteredCustomersByMobile = (searchText) => {
    if (!searchText) return userss;
    const searchLower = searchText.toLowerCase();
    return userss.filter((item) =>
      item.mobile.toLowerCase().includes(searchLower),
    );
  };

  // Function to handle customer mobile selection
  const handleCustomerMobileSelect = (selectedItem) => {
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerMobileDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);

    // Auto-fill other customer fields
    setCustomerName(selectedItem.fullName);
    setCustomerNameSearchText(selectedItem.fullName);
    setCustomerAddress(selectedItem.address);
    setCustomerArea(selectedItem.area || "");
    setCustomerCity(selectedItem.city);

    // Set all customer details
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
    });

    setIsNewCustomer(false);
  };

  // Handle key navigation in customer mobile dropdown
  const handleCustomerMobileKeyDown = (e) => {
    const filtered = getFilteredCustomersByMobile(customerMobileSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerMobileDropdownActive) {
          handleCustomerMobileSelect(filtered[customerMobileHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerMobileDropdownActive(false);
        break;
      case "Tab":
        setCustomerMobileDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer mobile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerMobileDropdownRef.current &&
        !customerMobileDropdownRef.current.contains(event.target) &&
        customerMobileInputRef.current &&
        !customerMobileInputRef.current.contains(event.target)
      ) {
        setCustomerMobileDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer mobile dropdown
  const renderCustomerMobileDropdown = () => {
    if (!customerMobileDropdownActive) return null;

    const filtered = getFilteredCustomersByMobile(customerMobileSearchText);

    // Reset refs array
    customerMobileItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerMobileInputRef.current
      ? customerMobileInputRef.current.offsetWidth
      : 150;
    const inputRect = customerMobileInputRef.current
      ? customerMobileInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    return ReactDOM.createPortal(
      <div
        ref={customerMobileDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.mobile.toLowerCase() ===
              customerMobileSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerMobileItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerMobileHighlightedIndex ? "active" : ""
                  }`}
                title={`${item.mobile} - ${item.fullName}`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerMobileHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerMobileHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s",
                }}
                onMouseEnter={() => setCustomerMobileHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerMobileSelect(item)}
              >
                {item.mobile} - {item.fullName}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching customers</div>
        )}
      </div>,
      document.getElementById("dropdown-portal"),
    );
  };

  // Handle customer name input change
  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    setCustomerNameSearchText(value);
    setCustomerNameDropdownActive(true);
    setCustomerNameHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered customers based on name
  const getFilteredCustomersByName = (searchText) => {
    if (!searchText) return CUSTOMERS;
    const searchLower = searchText.toLowerCase();
    return CUSTOMERS.filter((item) =>
      item.name.toLowerCase().includes(searchLower),
    );
  };
  // Function to handle customer name selection
  const handleCustomerNameSelect = (selectedItem) => {
    setCustomerName(selectedItem.name);
    setCustomerNameSearchText(selectedItem.name);
    setCustomerNameDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);

    // Auto-fill other customer fields
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerAddress(selectedItem.address);
    setCustomerArea(selectedItem.area || "");
    setCustomerCity(selectedItem.city);

    // Set all customer details
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
    });

    setIsNewCustomer(false);
  };

  // Handle key navigation in customer name dropdown
  const handleCustomerNameKeyDown = (e) => {
    const filtered = getFilteredCustomersByName(customerNameSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerNameDropdownActive) {
          handleCustomerNameSelect(filtered[customerNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerNameDropdownActive(false);
        break;
      case "Tab":
        setCustomerNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer name dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerNameDropdownRef.current &&
        !customerNameDropdownRef.current.contains(event.target) &&
        customerNameInputRef.current &&
        !customerNameInputRef.current.contains(event.target)
      ) {
        setCustomerNameDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer name dropdown
  const renderCustomerNameDropdown = () => {
    if (!customerNameDropdownActive) return null;

    const filtered = getFilteredCustomersByName(customerNameSearchText);

    // Reset refs array
    customerNameItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerNameInputRef.current
      ? customerNameInputRef.current.offsetWidth
      : 150;
    const inputRect = customerNameInputRef.current
      ? customerNameInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    return ReactDOM.createPortal(
      <div
        ref={customerNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.name.toLowerCase() === customerNameSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerNameHighlightedIndex ? "active" : ""
                  }`}
                title={`${item.name} - ${item.mobile}`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s",
                }}
                onMouseEnter={() => setCustomerNameHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerNameSelect(item)}
              >
                {item.name} - {item.mobile}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching customers</div>
        )}
      </div>,
      document.getElementById("dropdown-portal"),
    );
  };

  // Handle customer address change
  const handleCustomerAddressChange = (e) => {
    setCustomerAddress(e.target.value);
  };

  // Handle customer city change
  const handleCustomerCityChange = (e) => {
    setCustomerCity(e.target.value);
  };

  // Function to save a new customer

  const [paymentTypes, setPaymentTypes] = useState([]);

  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const paymentDropdownRef = useRef(null);

  // Initialize payment rows state with three common payment methods
  const [paymentRows, setPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash", // Pre-selected cash
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 2,
      pay_mode: "cheque", // Pre-selected cheque
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 3,
      pay_mode: "bank_transfer", // Pre-selected bank transfer
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const [payableRows, setPayableRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  // When bank accounts load, map __acctname__:* payment row placeholders to real bank ids (EditInvoice pattern)
  useEffect(() => {
    if (!Array.isArray(paymentTypes) || paymentTypes.length === 0) return;
    setPaymentRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (
          !row.bankDisplayName ||
          !row.type ||
          !String(row.type).startsWith("__acctname__:")
        ) {
          return row;
        }
        const match = paymentTypes.find(
          (b) =>
            String(b.label).trim().toLowerCase() ===
            String(row.bankDisplayName).trim().toLowerCase(),
        );
        if (match && String(match.value) !== String(row.type)) {
          changed = true;
          return { ...row, type: String(match.value) };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [paymentTypes]);

  const AVAILABLE_PAYMENT_METHODS = [
    "cash",
    "cheque",
    "bank_transfer",
    "upi",
    "card",
    "old_gold",
    "old_silver",
    "kasar",
    "scheme",
    "order",
    "os",
    "wallet", // Added wallet
  ];


  const getNextAvailableMethod = () => {
    const usedMethods = paymentRows.map((row) => row.pay_mode);
    return AVAILABLE_PAYMENT_METHODS.find(
      (method) => !usedMethods.includes(method),
    );
  };

  const addPaymentRow = () => {
    const newRow = {
      id: paymentRows.length + 1,
      pay_mode: "",
      type: "",
      remarks: "",
      amount: "",
    };
    setPaymentRows([...paymentRows, newRow]);
  };

  // Function to add a new payment row
  const handlePaymentRowChange = (rowId, field, value) => {
    // Auto-select type when Mode is "cash", "wallet", "kasar", "old_gold" or "old_silver"
    if (
      field === "pay_mode" &&
      ["cash", "wallet", "kasar", "old_gold", "old_silver"].includes(value)
    ) {
      let autoSelectedType = null;

      const findAccount = (keyword) =>
        bankAccounts.find(
          (b) =>
            (b.accountType &&
              (b.accountType.toLowerCase() === keyword ||
                b.accountType.toLowerCase() === keyword.replace("_", " "))) ||
            (b.bankName &&
              b.bankName.toLowerCase().includes(keyword.replace("_", " "))),
        );

      const account = findAccount(value);
      if (account) autoSelectedType = String(account.id);

      const updatedRows = paymentRows.map((row) =>
        row.id === rowId
          ? {
            ...row,
            [field]: value,
            type: autoSelectedType || "",
          }
          : row,
      );
      setPaymentRows(updatedRows);
      return;
    }

    const updatedRows = paymentRows.map((row) => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setPaymentRows(updatedRows);
  };

  // Auto-select payment types for initial rows once bank accounts are loaded
  useEffect(() => {
    if (bankAccounts.length > 0 && paymentRows.length > 0) {
      let hasChanges = false;
      const updatedRows = paymentRows.map((row) => {
        if (
          !row.type &&
          ["cash", "wallet", "kasar", "old_gold", "old_silver"].includes(
            row.pay_mode,
          )
        ) {
          const findAccount = (keyword) =>
            bankAccounts.find(
              (b) =>
                (b.accountType &&
                  (b.accountType.toLowerCase() === keyword ||
                    b.accountType.toLowerCase() ===
                    keyword.replace("_", " "))) ||
                (b.bankName &&
                  b.bankName.toLowerCase().includes(keyword.replace("_", " "))),
            );

          const account = findAccount(row.pay_mode);
          if (account) {
            hasChanges = true;
            return { ...row, type: String(account.id) };
          }
        }
        return row;
      });

      if (hasChanges) {
        setPaymentRows(updatedRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts, paymentRows]);

  // Function to handle focus on amount field
  const handleAmountFieldFocus = (rowId) => {
    // Check if this is the last row in the list
    if (rowId === paymentRows[paymentRows.length - 1].id) {
      // Add a new row when the amount field of the last row is focused
      const nextMethod = getNextAvailableMethod();
      if (nextMethod) {
        // Only add if there's an available payment method
        addPaymentRow();
      }
    }
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentTypes({
      ...paymentTypes,
      [type]: !paymentTypes[type],
    });
  };

  // Close payment dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setIsPaymentDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [paymentDropdownRef]);

  const getSelectedPaymentTypes = () => {
    const selected = Object.entries(paymentTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type, _]) => {
        // Format the payment type label
        if (type === "bankTransfer") return "Bank Transfer";
        return type.charAt(0).toUpperCase() + type.slice(1);
      });

    return selected.length > 0 ? selected.join(", ") : "Select Payment Type";
  };

  // Payment details state
  const [paymentDetails, setPaymentDetails] = useState({
    cash: { amount: "" },
    card: { amount: "" },
    upi: { amount: "" },
    bankTransfer: { amount: "" },
    other: { kashar: "" },
  });

  const handlePaymentDetailChange = (type, field, value) => {
    setPaymentDetails({
      ...paymentDetails,
      [type]: {
        ...paymentDetails[type],
        [field]: value,
      },
    });
  };

  // Payment completion tracking state
  const [followUpDate, setFollowUpDate] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");

  // Calculate total amount from payment rows
  const calculatePaymentTotal = () => {
    return paymentRows
      .reduce((sum, row) => {
        const amount = parseFloat(row.amount) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);
  };

  // Calculate remaining amount (Grand Total - Payment Total)
  const calculateRemainingAmount = () => {
    // Get grand total without the currency symbol
    const grandTotal = parseFloat(
      calculateGrandTotal().replace(/[^\d.-]/g, ""),
    );
    const paymentTotal = parseFloat(calculatePaymentTotal());

    const remaining = grandTotal - paymentTotal;
    // Return 0 if negative or very small number (accounting for floating point errors)
    return Math.max(0, remaining.toFixed(2));
  }
  const calculatePayableAmount = () => {
    const totalPaid = parseFloat(calculatePaymentTotal()) || 0;
    const grandTotal =
      parseFloat(String(calculateGrandTotal()).replace(/[^\d.-]/g, "")) || 0;
    if (totalPaid > grandTotal) {
      return (totalPaid - grandTotal).toFixed(2);
    }
    return "0.00";
  };

;

  // Add CSS to remove number input spinners
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Remove number input spinners */
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle round-off amount change
  const handleRoundOffChange = (e) => {
    handleRoundOffInputChange(e, setRoundOffAmount);
  };

  // Handle GST percentage change
  const handleGstPercentChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value) || value === "") {
      setGstPercent(value);
    }
  };

  // Auto-calculate GST Amount when GST % or Final Amount changes
  useEffect(() => {
    const finalAmount = calculateTotal();
    const gstPercentValue = parseFloat(gstPercent) || 0;
    const calculatedGstAmt = (finalAmount * gstPercentValue) / 100;
    setGstAmount(calculatedGstAmt.toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercent, tableData]);

  // Handle note change
  const handleNoteChange = (e) => {
    const value = e.target.value;
    if (value.length <= 70) {
      setNote(value);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    setTableData((prevData) => {
      const newData = prevData.filter((_, index) => index !== rowIndex);
      return newData;
    });
  };

  // Add expDueDate state
  const [expDueDate, setExpDueDate] = useState("");

  // Handler for Exp Due Date
  const handleExpDueDateChange = (e) => {
    setExpDueDate(e.target.value);
  };

  const locationState = location.state;
  const [hasFilledFromCustomOrder, setHasFilledFromCustomOrder] =
    useState(false);
  const [storedCustomOrderData, setStoredCustomOrderData] = useState(null);
  const customOrderUpdateIdempotencyKeyRef = useRef(null);

  const normalizeCustomOrderForEdit = (raw) => {
    const base = raw || {};
    const normalizedItems = Array.isArray(base.custom_order_entry)
      ? base.custom_order_entry
      : Array.isArray(base.custom_order)
        ? base.custom_order
        : Array.isArray(base.entries)
          ? base.entries
          : Array.isArray(base.items)
            ? base.items
            : [];

    return {
      ...base,
      custom_order_entry: normalizedItems,
      user_id: base.user_id ?? base.customer_id ?? "",
      name: base.name ?? base.customer_name ?? base.user_name ?? "",
      mobile: base.mobile ?? base.customer_mobile ?? base.user_mobile ?? "",
      exp_delivery: base.exp_delivery ?? base.expected_delivery ?? "",
      final_amt: base.final_amt ?? base.final_amount ?? base.grand_total ?? 0,
      total: base.total ?? base.grand_total ?? base.final_amount ?? 0,
      note: base.note ?? base.notes ?? "",
      payments:
        Array.isArray(base.payments) && base.payments.length > 0
          ? base.payments
          : Array.isArray(base.payment_details)
            ? base.payment_details
            : [],
    };
  };

  // Function to process product group data
  const processProductGroupData = (customOrderData) => {
    // Map the table data with product group names
    const mappedTableData = customOrderData.custom_order_entry.map(
      (row, idx) => ({
        id: idx + 1,
        data: COLUMNS.map((col) => {
          let value = "";
          let extra = {};
          switch (col.id) {
            case "tag_no":
              value = row.tag_no || "";
              break;
            case "productGroup":
              value = row.product_group_name || "";
              break;
            case "design":
              value = row.design || "";
              break;
            case "size":
              value = row.size || "";
              break;
            case "pcs":
              value = row.pcs || "";
              break;
            case "grWt":
              value = row.gwt || "";
              break;
            case "lsWt":
              value = row.lwt || "";
              break;
            case "netWt":
              value = row.nwt || "";
              break;
            case "itRateOn":
              value = row.itrateon || "NWt";
              break;
            case "itRate":
              value = row.itrate || "";
              break;
            case "itAmt":
              value = row.itamt || "";
              break;
            case "lType":
              value = row.lbr_type || "";
              break;
            case "lbrPercentage":
              value = row.lbr_percent || "";
              break;
            case "lbrRate":
              value = row.lbr_rate ?? row.rate ?? "";
              break;
            case "lbrAmt":
              value = row.lbr_amt ?? row.making_charge ?? "";
              break;
            case "othAmt":
              value = row.othamt ?? row.oth_amt ?? "";
              extra.otherAmountEntries = mapApiChargesToOtherAmountEntries(
                row.charges,
              );
              break;
            case "mrp":
              value = row.mrp || "";
              break;
            case "image":
              value = row.image || "";
              break;
            case "totalAmt":
              value = row.totalamt ?? row.total_amount ?? "";
              break;
            default:
              value = "";
          }
          if (col.id === "image") {
            return {
              id: col.id,
              value,
              imageFile: null,
              imagePreview: value ? `${node_imageBaseUrl}${value}` : null,
            };
          }
          if (col.id === "othAmt") {
            return { id: col.id, value, ...extra };
          }
          return { id: col.id, value };
        }),
      }),
    );
    setTableData(mappedTableData);
    seedProductGroupsFromOrderItems(customOrderData);
  };

  const applyCustomOrderPaymentsToRows = (payments) => {
    const mappedPayments = mapShowPaymentsToCustomOrderPaymentRows(payments);
    if (mappedPayments?.length > 0) {
      setPaymentRows(mappedPayments);
    }
  };

  // Fetch custom order data using the ID
  const fetchCustomOrderData = async (customOrderId) => {
    productGroupUserSearchRef.current = false;
    setLastSearchedProductGroupPrefix("");
    setProductGroupApiCalled(false);
    setProductGroupPoolHeader(null);
    setDropdownData((prev) => ({ ...prev, productGroup: [] }));

    try {
      const res = await requestCustomOrderShowForEdit(customOrderId);

      if (
        (isErpApiSuccess(res) || res?.message === "radha") &&
        (res?.data != null || res?.custom_order_details != null)
      ) {
        const rawCustomOrderData = resolveCustomOrderShowPayload(res);
        const customOrderData = normalizeCustomOrderForEdit(rawCustomOrderData);
        const customOrderTable = customOrderData.custom_order_entry || [];
        applyCustomOrderPaymentsToRows(customOrderData.payments);

        setCustomerName(customOrderData.name || "");
        setCustomerNameSearchText(customOrderData.name || "");
        setCustomerMobile(customOrderData.mobile || "");
        setCustomerMobileSearchText(customOrderData.mobile || "");
        setSelectedCustomerId(customOrderData.user_id || "");

        // Set customer address and area
        setCustomerAddress(customOrderData.address || "");
        setCustomerArea(customOrderData.area || "");
        setCustomerCity(customOrderData.city || "");

        // Set customer details including balance
        setCustomerDetails((prev) => ({
          ...prev,
          balance:
            customOrderData.users_balance ??
            customOrderData.balance ??
            "0",
          saleBalance:
            customOrderData.pending_balance ||
            customOrderData.sale_balance ||
            "0",
          email: customOrderData.email || "",
          gstNumber: customOrderData.GST_number || "",
          panNo: customOrderData.pan_no || "",
          aadharNo: customOrderData.aadhar_no || "",
          companyName: customOrderData.company_name || "",
          companyAddress: customOrderData.company_address || "",
          state: customOrderData.state || "",
        }));

        // Set expected delivery date
        if (customOrderData.exp_delivery) {
          setExpDate(customOrderData.exp_delivery);
        }

        const loadedFollowup =
          customOrderData.followup_date || customOrderData.follow_up_date || "";
        if (loadedFollowup) {
          setFollowUpDate(String(loadedFollowup).slice(0, 10));
        } else {
          setFollowUpDate("");
        }

        // Set round off amount
        if (
          customOrderData.round_off !== undefined &&
          customOrderData.round_off !== null
        ) {
          setRoundOffAmount(String(customOrderData.round_off));
        }

        // Set GST percentage
        if (
          customOrderData.gst_percent !== undefined &&
          customOrderData.gst_percent !== null
        ) {
          setGstPercent(String(customOrderData.gst_percent));
        } else {
          setGstPercent("3"); // Default to 3%
        }

        // Set GST amount
        if (
          customOrderData.gst_amount !== undefined &&
          customOrderData.gst_amount !== null
        ) {
          setGstAmount(String(customOrderData.gst_amount));
        } else {
          // Will be calculated by useEffect after tableData is loaded
          setGstAmount("0.00");
        }

        // Set note
        if (
          customOrderData.note !== undefined &&
          customOrderData.note !== null
        ) {
          setNote(customOrderData.note || "");
        }

        const billTypeId =
          customOrderData.bill_type_id ??
          customOrderData.billTypeId ??
          customOrderData.billtype_id;
        const billTypeName =
          customOrderData.bill_type_name ??
          customOrderData.billTypeName ??
          customOrderData.billtype_name;
        const estimate =
          customOrderData.estimate ??
          customOrderData.is_estimate ??
          customOrderData.estimate_flag ??
          null;
        const normalizedBillTypeName = String(billTypeName || "").trim();
        const matchedBillType =
          billTypes.find(
            (item) =>
              (billTypeId != null &&
                String(item.id) === String(billTypeId)) ||
              (normalizedBillTypeName &&
                item.name.toLowerCase() === normalizedBillTypeName.toLowerCase()),
          ) || null;

        if (matchedBillType) {
          selectedBillTypeIdRef.current = String(matchedBillType.id);
          setSelectedBillTypeId(String(matchedBillType.id));
          setSelectedBillTypeName(matchedBillType.name || "GST");
        } else {
          if (billTypeId != null && String(billTypeId).trim() !== "") {
            selectedBillTypeIdRef.current = String(billTypeId);
            setSelectedBillTypeId(String(billTypeId));
          }
          if (normalizedBillTypeName) {
            setSelectedBillTypeName(normalizedBillTypeName);
          }
        }
        if (estimate != null) {
          setEstimateValue(Number(estimate) || 0);
        } else if (normalizedBillTypeName) {
          setEstimateValue(
            normalizedBillTypeName.toLowerCase() === "quotation" ? 1 : 0,
          );
        }

        // Store custom order data for later processing when dropdown data is available
        setStoredCustomOrderData(customOrderData);

        if (
          customOrderData.custom_order_entry &&
          Array.isArray(customOrderData.custom_order_entry)
        ) {
          processProductGroupData(customOrderData);
        }

        setHasFilledFromCustomOrder(true);
        return res.data;
      } else {
        toast.error("Failed to fetch custom order data");
        return null;
      }
    } catch (error) {
      toast.error("Error fetching custom order data");
      return null;
    }
  };

  // Call the API when component mounts if customOrderId is available
  useEffect(() => {
    if (locationState?.customOrderId) {
      fetchCustomOrderData(locationState.customOrderId);
    }
  }, [locationState?.customOrderId]);

  // Re-apply payments when stored order payload updates (covers late API shapes)
  useEffect(() => {
    if (!storedCustomOrderData?.payments?.length) return;
    applyCustomOrderPaymentsToRows(storedCustomOrderData.payments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCustomOrderData]);

  // Removed dependency on external product group API; table is populated directly from order data

  useFillSalesFromCustomOrder({
    locationState,
    setTableData,
    setPaymentRows,
    setRoundOffAmount,
    setNote,
    COLUMNS,
    dropdownData,
    hasFilledFromCustomOrder,
    setHasFilledFromCustomOrder,
    userss,
    setCustomerName,
    setCustomerNameSearchText,
    setCustomerMobile,
    setCustomerMobileSearchText,
    setSelectedCustomerId,
    setCustomerDetails,
    setCustomerCity,
    setCustomerAddress,
    setCustomerArea,
    setSelectedState,
    states,
    cities,
    setExpDate,
    expDate,
    isEditMode: !!locationState?.customOrderId, // Add flag to indicate edit mode
  });

  useEffect(() => {
    if (locationState) {
    }
  }, [locationState]);

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center flex-wrap gap-3 w-100">
              <h4 className="card-title mb-0">Edit Custom Order Entry</h4>
              <div className="d-flex align-items-center flex-shrink-0">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 text-nowrap"
                    style={{
                      minWidth: "74px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    Bill Type:
                  </label>
                  <div style={billTypeHeaderSelectWrapStyle}>
                    <Select
                      styles={billTypeHeaderSelectStyles}
                      menuPlacement="bottom"
                      isSearchable={false}
                      options={billTypes
                        .filter(
                          (item) =>
                            String(item.id) !== String(selectedBillTypeId || ""),
                        )
                        .map((item) => ({
                          value: String(item.id),
                          label: item.name,
                        }))}
                      value={
                        (() => {
                          const selected =
                            billTypes.find(
                              (item) =>
                                String(item.id) === String(selectedBillTypeId),
                            ) ||
                            billTypes.find(
                              (item) =>
                                item.name.toLowerCase() ===
                                String(selectedBillTypeName || "").toLowerCase(),
                            ) ||
                            billTypes.find(
                              (item) => item.name.toLowerCase() === "gst",
                            ) ||
                            null;
                          return selected
                            ? {
                                value: String(selected.id),
                                label: selected.name,
                              }
                            : selectedBillTypeName
                              ? {
                                  value: String(selectedBillTypeId || ""),
                                  label: selectedBillTypeName,
                                }
                              : null;
                        })()
                      }
                      onChange={(option) => {
                        const selected = billTypes.find(
                          (item) => String(item.id) === String(option?.value),
                        );
                        applyBillTypeSelection(selected);
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Date Component */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 fw-semibold text-nowrap"
                  style={{ fontSize: "0.85rem" }}
                >
                  Date:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={billDate}
                  onChange={handleDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              {/* Exp Date Component */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 fw-semibold text-nowrap"
                  style={{ fontSize: "0.85rem" }}
                >
                  Exp Delivery:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={expDate}
                  onChange={handleExpDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              <ProductGroupPoolBalance balance={productGroupPoolHeader} />
              {/* {selectedCustomerId && (
                <div classNamw="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  <span
                    className={`badge ${
                      parseFloat(customerDetails.balance) >= 0
                        ? "bg-success-subtle text-success"
                        : "bg-danger-subtle text-danger"
                    }`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Balance: ₹
                    {parseFloat(customerDetails.balance || 0).toFixed(2)}
                  </span>
                  <span
                    className={`badge ${
                      parseFloat(customerDetails.saleBalance) >= 0
                        ? "bg-success-subtle text-success"
                        : "bg-danger-subtle text-danger"
                    } ms-2`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Pending: ₹
                    {parseFloat(customerDetails.saleBalance || 0).toFixed(2)}
                  </span>
                </div>
              )} */}
              {selectedCustomerId && (
                <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  <span
                    className={`badge ${parseFloat(customerDetails.balance) >= 0
                      ? "bg-success-subtle text-success"
                      : "bg-danger-subtle text-danger"
                      }`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Balance: ₹{" "}
                    {parseFloat(customerDetails.balance || 0).toFixed(2)}
                  </span>
                  <span
                    className={`badge ${parseFloat(customerDetails.saleBalance) > 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-success-subtle text-success"
                      } ms-2`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Pending: ₹{" "}
                    {parseFloat(customerDetails.saleBalance || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-1">
              {/* Customer Mobile - properly sized with adequate spacing */}
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "80px", flexShrink: 0 }}
                  >
                    Customer:
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={customerMobileInputRef}
                      {...mobileInputFieldProps}
                      value={customerMobileSearchText}
                      onChange={handleCustomerMobileChange}
                      onKeyDown={handleCustomerMobileKeyDown}
                      onClick={() => setCustomerMobileDropdownActive(true)}
                      className={`form-control form-control-sm ${buttonClass}`}
                      onFocus={() => {
                        const inputElement = customerMobileInputRef.current;
                        if (inputElement) {
                          inputElement.style.backgroundColor = "#fff";
                        }
                        setCustomerMobileDropdownActive(true);
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                      autoComplete="off"
                      placeholder="Mobile No"
                    />
                    {renderCustomerMobileDropdown()}
                  </div>
                </div>
              </div>

              {/* Customer Name with inline controls */}
              <div className="col-lg-3 col-md-7 col-sm-12 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={customerNameInputRef}
                      type="text"
                      value={customerNameSearchText}
                      onChange={(e) => {
                        setCustomerNameSearchText(e.target.value);
                      }}
                      onKeyDown={handleCustomerNameKeyDown}
                      onClick={() => setCustomerNameDropdownActive(true)}
                      className={`form-control form-control-sm ${buttonClass}`}
                      onFocus={() => {
                        const inputElement = customerNameInputRef.current;
                        if (inputElement) {
                          inputElement.style.backgroundColor = "#fff";
                        }
                        setCustomerNameDropdownActive(true);
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                      autoComplete="off"
                      placeholder="Name"
                    />
                    {renderCustomerNameDropdown()}
                  </div>
                </div>
              </div>

              {/* Customer Address */}
              <div className="col-lg-2 col-md-3 col-sm-6 mb-2">
                <input
                  type="text"
                  readOnly
                  value={customerAddress}
                  className={`form-control form-control-sm ${buttonClass}`}
                  style={{
                    fontSize: "0.85rem",
                    backgroundColor: "transparent",
                    transition: "background-color 0.2s",
                  }}
                  placeholder="Address"
                />
              </div>

              {/* Customer Area */}
              <div className="col-lg-2 col-md-3 col-sm-6 mb-2">
                <input
                  type="text"
                  readOnly
                  value={customerArea}
                  className={`form-control form-control-sm ${buttonClass}`}
                  style={{
                    fontSize: "0.85rem",
                    backgroundColor: "transparent",
                    transition: "background-color 0.2s",
                  }}
                  placeholder="Area"
                />
              </div>

              {/* Customer Details Modal */}

              {/* City and Save Button Container - Adjust city visibility based on isNewCustomer */}
            </div>

            <div
              className={`table-responsive border rounded-0 mt-3 ${buttonClass}`}
            >
              <table
                className={`table table-bordered table-hover table-sm mb-0 theme-header-table ${tableClass}`}
              >
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  onMouseEnter={(e) => {
                                    e.target.textContent = "×";
                                    e.target.style.fontWeight = "600";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.textContent = rowIndex + 1;
                                    e.target.style.fontWeight = "normal";
                                  }}
                                >
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;
                        const hasFieldError = !!itemFieldErrors[row.id]?.includes(
                          cell.id,
                        );

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                              ...lineCellErrorStyle(hasFieldError),
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    const columnType = getColumnType(colIndex);
                                    if (columnType === "productGroup") {
                                      productGroupUserSearchRef.current = true;
                                    }
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue,
                                    );
                                    // Show dropdown while typing; restrict for productGroup
                                    if (columnType === "productGroup") {
                                      const hasEnoughChars =
                                        (newValue || "").trim().length >= 3;
                                      if (
                                        hasEnoughChars &&
                                        (getFilteredItems(newValue, "productGroup")
                                          .length > 0 ||
                                          productGroupApiCalled)
                                      ) {
                                        if (
                                          activeDropdown.row === rowIndex &&
                                          activeDropdown.col === colIndex &&
                                          activeDropdown.type === columnType
                                        ) {
                                          handleDropdownClick(
                                            rowIndex,
                                            colIndex,
                                            columnType,
                                          );
                                        } else {
                                          setActiveDropdown({
                                            row: rowIndex,
                                            col: colIndex,
                                            type: columnType,
                                          });
                                        }
                                      } else {
                                        setActiveDropdown({
                                          row: null,
                                          col: null,
                                          type: null,
                                        });
                                      }
                                    } else {
                                      if (
                                        activeDropdown.row === rowIndex &&
                                        activeDropdown.col === colIndex &&
                                        activeDropdown.type === columnType
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      } else {
                                        setActiveDropdown({
                                          row: rowIndex,
                                          col: colIndex,
                                          type: columnType,
                                        });
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    if (columnType === "productGroup") {
                                      const cell =
                                        tableData[rowIndex]?.data?.[colIndex];
                                      const value = (cell?.value || "")
                                        .toString()
                                        .trim();
                                      if (
                                        value.length >= 3 &&
                                        (getFilteredItems(value, "productGroup")
                                          .length > 0 ||
                                          productGroupApiCalled)
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      }
                                    } else {
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType,
                                      );
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                  (fileInputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    // Check if file is an image
                                    if (!file.type.match("image.*")) {
                                      alert("Please select an image file");
                                      return;
                                    }

                                    // Check file size (limit to 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert("File size should not exceed 5MB");
                                      return;
                                    }

                                    handleImageUpload(file, rowIndex);
                                  }}
                                />
                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current,
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current,
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";
                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300,
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(cell.imagePreview);
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `tooltip-${rowIndex}-${colIndex}`
                                      ] = el)
                                      }
                                      className="shadow rounded p-3 bg-white"
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current,
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current,
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex,
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image",
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(cell.imagePreview);
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />

                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current,
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current,
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `oth-amt-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                          boxShadow:
                                            "-2px -2px 2px rgba(0,0,0,0.1)",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                    cell.otherAmountEntries
                                                      .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹{" "}
                                                {Number(entry.amount).toFixed(
                                                  2,
                                                )}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹{" "}
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0,
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Special handling for design field - allow alphanumeric
                                  if (columnType === "design") {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                }}
                                // disabled={COLUMNS[colIndex].id === "grWt"}
                                className={`form-control border-0 rounded-0 ${buttonClass}`}
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row */}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    {/* No. */}
                    <td className="p-0" style={{ width: COLUMNS[0]?.width }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Product Group */}
                    <td className="p-0"></td> {/* Design */}
                    <td className="p-0"></td> {/* Size */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Pcs */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.reduce((sum, row) => {
                            const pcs = parseFloat(
                              row.data.find((cell) => cell.id === "pcs")
                                ?.value || 0,
                            );
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          }, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* GWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const gwt = parseFloat(
                                row.data.find((cell) => cell.id === "grWt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(gwt) ? 0 : gwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* LWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lwt = parseFloat(
                                row.data.find((cell) => cell.id === "lsWt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(lwt) ? 0 : lwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* NWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const nwt = parseFloat(
                                row.data.find((cell) => cell.id === "netWt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(nwt) ? 0 : nwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* ItRateOn */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItRate */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {/* Average item rate - not sum */}
                          {tableData.length > 0
                            ? (
                              tableData.reduce((sum, row) => {
                                const itRate = parseFloat(
                                  row.data.find(
                                    (cell) => cell.id === "itRate",
                                  )?.value || 0,
                                );
                                return sum + (isNaN(itRate) ? 0 : itRate);
                              }, 0) / tableData.length
                            ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const itAmt = parseFloat(
                                row.data.find((cell) => cell.id === "itAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(itAmt) ? 0 : itAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Lbr Type */}
                    <td className="p-0"></td> {/* Lbr % */}
                    <td className="p-0"></td> {/* Lbr Rate */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr Amt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrAmt = parseFloat(
                                row.data.find((cell) => cell.id === "lbrAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(lbrAmt) ? 0 : lbrAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* OthAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const othAmt = parseFloat(
                                row.data.find((cell) => cell.id === "othAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(othAmt) ? 0 : othAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* MRP */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const mrp = parseFloat(
                                row.data.find((cell) => cell.id === "mrp")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(mrp) ? 0 : mrp);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Image */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* TotalAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const totalAmt = parseFloat(
                                row.data.find((cell) => cell.id === "totalAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(totalAmt) ? 0 : totalAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-end mt-3">
              {/* <button className="btn btn-primary" onClick={handleSubmit}>
                Add
              </button> */}
            </div>


            <PaymentSection
              paymentRows={paymentRows}
              onPaymentRowsChange={setPaymentRows}
              paymentTypes={paymentTypes}
              bankAccounts={bankAccounts}
              payableRows={payableRows}
              onPayableRowsChange={setPayableRows}
              note={note}
              onNoteChange={setNote}
              finalAmount={calculateTotal().toFixed(2)}
              showDiscount={false}
              gstPercentage={gstPercent}
              onGstPercentageChange={setGstPercent}
              gstAmount={
                typeof gstAmount === "string"
                  ? gstAmount
                  : Number(gstAmount || 0).toFixed(2)
              }
              roundOffAmount={roundOffAmount}
              onRoundOffChange={handleRoundOffChange}
              grandTotal={calculateGrandTotal()}
              totalPaid={parseFloat(calculatePaymentTotal()) || 0}
              payableAmount={calculatePayableAmount()}
              remainingAmount={calculateRemainingAmount()}
              followUpDate={followUpDate}
              onFollowUpDateChange={setFollowUpDate}
              buttonClass={buttonClass}
              cardClass={cardClass}
              buttonTableStyle={buttonTableStyle}
            />

            {/* Submit button at the bottom */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="printCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="printCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m10-8V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
                            <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Print</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="pdfCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4M5 18h1.5a1.5 1.5 0 0 0 0-3H5v6m12-3h2m1-3h-3v6m-6-6v6h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">PDF</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="whatsappCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="whatsappCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">
                          WhatsApp
                        </span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="emailCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <path d="m3 7l9 6l9-6" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Email</span>
                      </label>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`btn btn-primary ${buttonClass}`}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderImageModal()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={rateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={sumOtherAmountEntries}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableData}
      />
      {/* <p>Master Gold Rate: {masterGoldRate}</p>
      <p>Master Silver Rate: {masterSilverRate}</p> */}
      <Toaster position="top-center" />
    </div>
  );
};

export default EditCustomOrderEntry;
